/**
 * Cola de salida.
 *
 * Todo lo que se hace sin conexión entra aquí y se queda esperando. **El orden
 * importa**: si creas un movimiento y luego lo editas, la edición tiene que
 * enviarse después de la creación, o el servidor intentaría editar algo que aún no
 * existe.
 *
 * Cada operación lleva su clave de idempotencia desde el momento en que se encola,
 * no cuando se envía. Es lo que hace que reintentar sea inofensivo: la clave es la
 * misma en el primer intento y en el quinto.
 */

import type { SyncOperation, SyncOperationType } from '@eldescuadre/contracts';

import { STORES, getAll, getMeta, put, removeMany, setMeta } from './db.js';

/** Una operación en la cola, con lo necesario para poder reintentarla. */
export interface QueuedOperation extends SyncOperation {
  /** Cuántas veces se ha intentado enviar. */
  attempts: number;
  /** Último error, si lo hubo. Sirve para avisar al usuario. */
  lastError?: string | undefined;
}

const OUTBOX_KEY = 'outbox';

/** Añade una operación a la cola y devuelve la operación completa. */
export async function enqueue(
  type: SyncOperationType,
  entityId: string,
  payload: Record<string, unknown> = {},
): Promise<QueuedOperation> {
  const operation: QueuedOperation = {
    id: crypto.randomUUID(),
    type,
    entityId,
    // La clave se fija ahora y no cambia nunca: eso es lo que hace seguro el reintento.
    idempotencyKey: `${type}:${entityId}:${crypto.randomUUID()}`,
    queuedAt: new Date().toISOString(),
    payload,
    attempts: 0,
  };

  await put(STORES.outbox, operation);
  return operation;
}

/** Todo lo que está pendiente, en el orden en que se hizo. */
export async function pendingOperations(): Promise<QueuedOperation[]> {
  const all = await getAll<QueuedOperation>(STORES.outbox);
  return all.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

/** Cuántas operaciones hay esperando. */
export async function pendingCount(): Promise<number> {
  return (await getAll<QueuedOperation>(STORES.outbox)).length;
}

/** Quita de la cola las operaciones que ya se aplicaron. */
export async function clearApplied(ids: readonly string[]): Promise<void> {
  await removeMany(STORES.outbox, ids);
}

/** Anota que un intento falló, para poder avisar y no reintentar sin fin. */
export async function markFailed(id: string, error: string): Promise<void> {
  const { get } = await import('./db.js');
  const operation = await get<QueuedOperation>(STORES.outbox, id);
  if (!operation) return;

  await put(STORES.outbox, {
    ...operation,
    attempts: operation.attempts + 1,
    lastError: error,
  });
}

/** Vacía la cola. Se usa al cerrar sesión. */
export async function clearOutbox(): Promise<void> {
  const all = await getAll<QueuedOperation>(STORES.outbox);
  await removeMany(
    STORES.outbox,
    all.map((operation) => operation.id),
  );
}

/** Lee el cursor de sincronización guardado. */
export async function readCursor(): Promise<string | null> {
  return (await getMeta<string>(OUTBOX_KEY + ':cursor')) ?? null;
}

/** Guarda el cursor de sincronización. */
export async function writeCursor(cursor: string): Promise<void> {
  await setMeta(OUTBOX_KEY + ':cursor', cursor);
}

/** Guarda cuándo se sincronizó por última vez, para poder mostrarlo. */
export async function writeLastSync(at: string): Promise<void> {
  await setMeta(OUTBOX_KEY + ':lastSync', at);
}

/** Lee cuándo se sincronizó por última vez. */
export async function readLastSync(): Promise<string | null> {
  return (await getMeta<string>(OUTBOX_KEY + ':lastSync')) ?? null;
}
